import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import styles from './index.module.scss'
import Header from '../../components/Header'
import DropDown from '../../components/DropDown'
import Avatar from '../../components/Avatar'
import Divider from '../../components/Divider'
import DotsVerticalIcon from '../../assets/icon/DotsVerticalIcon'
import TrashIcon from '../../assets/icon/TrashIcon'
import FilterCloseIcon from '../../assets/icon/FilterCloseIcon'
import MarkAsReadIcon from '../../assets/icon/MarkAsReadIcon'
import OpenSourceIcon from '../../assets/icon/OpenSourceIcon'
import BellUnreadIcon from '../../assets/icon/BellUnreadIcon'
import SortArrowsIcon from '../../assets/icon/SortArrowsIcon'
import TaskAssignedIcon from '../../assets/icon/TaskAssignedIcon'
import StatusChangeIcon from '../../assets/icon/StatusChangeIcon'
import TaskDelegatedIcon from '../../assets/icon/TaskDelegatedIcon'
import PaperclipIcon from '../../assets/icon/PaperclipIcon'
import PriorityChangeIcon from '../../assets/icon/PriorityChangeIcon'
import StarsIcon from '../../assets/icon/StarsIcon'
import DeadlineIcon from '../../assets/icon/DeadlineIcon'
import CommentIcon from '../../assets/icon/CommentIcon'
import MentionIcon from '../../assets/icon/MentionIcon'
import {
    MOCK_NOTIFICATIONS,
    NOTIFICATION_GROUP_LABELS,
    NOTIFICATION_GROUP_ORDER,
    NOTIFICATION_TABS,
    NotificationGroup,
    NotificationMeta,
    NotificationRow,
    NotificationTab,
    NotificationType,
} from './utils/mock'
import { formatNotificationTime, mapNotifications } from './utils/adapter'
import {
    NotificationPrefs,
    NotificationSort,
    readNotificationPrefs,
    writeNotificationPrefs,
} from './utils/notificationPrefs'
import { NotificationService } from '../../services'
import ProjectService from '../../services/project.service'
import TaskService from '../../services/task.service'
import AttachmentService from '../../services/attachment.service'
import { fetchAttachmentObjectUrl } from '../../helpers/attachmentImage'
import { useWorkspace } from '../../context/workspace.context'
import { ensureWorkspaceForEntity, fetchProjectWorkspaceId } from '../../utils/workspaceNavigation'
import { getProjectStatusColor } from '../../utils/projectStatus'

const SORT_OPTIONS: { value: NotificationSort; label: string }[] = [
    { value: 'alpha_asc', label: 'Алфавит: А-Я' },
    { value: 'alpha_desc', label: 'Алфавит: Я-А' },
    { value: 'date_desc', label: 'Дата изменения: от новых к старым' },
    { value: 'date_asc', label: 'Дата изменения: от старых к новым' },
]

const TYPE_ICONS: Record<NotificationType, React.ReactNode> = {
    assign: <TaskAssignedIcon />,
    status: <StatusChangeIcon />,
    delegate: <TaskDelegatedIcon />,
    attach: <PaperclipIcon />,
    priority: <PriorityChangeIcon />,
    deadline: <DeadlineIcon />,
    comment: <CommentIcon />,
    mention: <MentionIcon />,
}

function ChevronDownIcon() {
    return (
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6 9.5L12 15.5L18 9.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

function ArrowSeparator() {
    return (
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 6L15 12L9 18" stroke="#B1AEC7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    )
}

function NotificationAvatar({ row }: { row: NotificationRow }) {
    if (row.warning) {
        return (
            <span className={styles.notifications__warningAvatar}>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 6.5V13.5" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" />
                    <circle cx="12" cy="17.2" r="1.4" fill="#fff" />
                </svg>
            </span>
        )
    }

    return (
        <div className={styles.notifications__avatar}>
            <Avatar user={{ full_name: row.author, photo: row.avatarUrl }} />
        </div>
    )
}

function NotificationMetaBadges({ meta, onOpenFile }: { meta: NotificationMeta; onOpenFile?: () => void }) {
    if (meta.kind === 'status') {
        return (
            <span className={styles.notifications__metaGroup}>
                <span className={styles.notifications__statusBadge}>
                    <span className={styles.notifications__dot} style={{ background: meta.fromColor || '#B1AEC7' }} />
                    {meta.from}
                </span>
                <ArrowSeparator />
                <span className={styles.notifications__statusBadge}>
                    <span className={styles.notifications__dot} style={{ background: meta.toColor || '#B1AEC7' }} />
                    {meta.to}
                </span>
            </span>
        )
    }

    if (meta.kind === 'priority') {
        return (
            <span className={styles.notifications__metaGroup}>
                <span className={styles.notifications__priorityBadge}>
                    <StarsIcon color={meta.fromColor || '#B1AEC7'} />
                    {meta.from}
                </span>
                <ArrowSeparator />
                <span className={styles.notifications__priorityBadge}>
                    <StarsIcon color={meta.toColor || '#B1AEC7'} />
                    {meta.to}
                </span>
            </span>
        )
    }

    return (
        <span
            className={`${styles.notifications__fileBadge} ${onOpenFile ? styles.notifications__fileBadgeClickable : ''}`}
            onClick={onOpenFile ? (event) => {
                event.stopPropagation()
                onOpenFile()
            } : undefined}
            title={meta.label}
        >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <rect x="4" y="3" width="16" height="18" rx="3" fill="#453CFB" />
                <path d="M8 8h8M8 12h8M8 16h5" stroke="#fff" strokeWidth="1.4" strokeLinecap="round" />
            </svg>
            {meta.label}
        </span>
    )
}

export default function Notifications() {
    const navigate = useNavigate()
    const { workspace, workspaces, setWorkspace, fetchWorkspaces } = useWorkspace()
    const prefsRef = useRef<NotificationPrefs>(readNotificationPrefs())
    const [activeTab, setActiveTab] = useState<NotificationTab>('task')
    const [unreadOnly, setUnreadOnly] = useState(prefsRef.current.unreadOnly)
    const [sort, setSort] = useState<NotificationSort>(prefsRef.current.sort)
    const [notifications, setNotifications] = useState<NotificationRow[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [nowMs, setNowMs] = useState(() => Date.now())

    // Обновляем "N минут назад" каждые 30 секунд, пока страница открыта.
    useEffect(() => {
        const timerId = setInterval(() => setNowMs(Date.now()), 30 * 1000)

        return () => clearInterval(timerId)
    }, [])

    const persistPrefs = (next: Partial<NotificationPrefs>) => {
        prefsRef.current = { ...prefsRef.current, ...next }
        writeNotificationPrefs(prefsRef.current)
    }

    // Статусы проектов (key -> название/цвет) кешируются на время жизни страницы.
    const projectStatusesRef = useRef(new Map<string, Map<string, { title: string; color: string }>>())

    // В details статусы приходят сырыми ключами (backlog, status_...) —
    // подтягиваем их названия и цвета из статусов соответствующего проекта.
    const resolveStatusMeta = useCallback(async (rows: NotificationRow[]) => {
        const projectIds = Array.from(new Set(
            rows
                .filter((row) => row.meta?.kind === 'status' && row.projectId && (row.meta.fromKey || row.meta.toKey))
                .map((row) => String(row.projectId)),
        ))

        if (projectIds.length === 0) {
            return
        }

        await Promise.all(projectIds.map(async (projectId) => {
            if (projectStatusesRef.current.has(projectId)) {
                return
            }

            try {
                const statuses = await ProjectService.getSortedTaskStatuses(projectId)
                const statusMap = new Map<string, { title: string; color: string }>()

                statuses.forEach((status) => {
                    statusMap.set(String(status.key), {
                        title: status.name || status.title || String(status.key),
                        color: getProjectStatusColor(status.key, status.color),
                    })
                })

                projectStatusesRef.current.set(projectId, statusMap)
            } catch {
                // Проект недоступен (нет прав/удалён) — оставляем ключи как есть.
                projectStatusesRef.current.set(projectId, new Map())
            }
        }))

        setNotifications((prev) => prev.map((row) => {
            if (row.meta?.kind !== 'status' || !row.projectId) {
                return row
            }

            const statusMap = projectStatusesRef.current.get(String(row.projectId))
            const from = row.meta.fromKey ? statusMap?.get(row.meta.fromKey) : undefined
            const to = row.meta.toKey ? statusMap?.get(row.meta.toKey) : undefined

            if (!from && !to) {
                return row
            }

            return {
                ...row,
                meta: {
                    ...row.meta,
                    from: from?.title ?? row.meta.from,
                    fromColor: from?.color ?? row.meta.fromColor,
                    to: to?.title ?? row.meta.to,
                    toColor: to?.color ?? row.meta.toColor,
                },
            }
        }))
    }, [])

    const loadNotifications = useCallback(async () => {
        setLoading(true)
        setError(null)

        try {
            const { notifications: rows } = await NotificationService.list({ limit: 50 })

            // Пока бэкенд не отдаёт уведомлений — показываем демо-набор из макета.
            if (rows.length === 0) {
                console.info('[notifications] Бэкенд вернул пустой список — показан демо-набор')
                setNotifications(MOCK_NOTIFICATIONS)
            } else {
                const mapped = mapNotifications(rows)
                setNotifications(mapped)
                resolveStatusMeta(mapped)
            }
        } catch (e: any) {
            const status = e?.status ?? e?.response?.status
            console.error('[notifications] Не удалось загрузить уведомления', e)
            setError(status ? `Ошибка ${status}` : 'Ошибка сети')
        } finally {
            setLoading(false)
        }
    }, [resolveStatusMeta])

    useEffect(() => {
        loadNotifications()
    }, [loadNotifications])

    const handleToggleUnread = () => {
        setUnreadOnly((prev) => {
            const next = !prev
            persistPrefs({ unreadOnly: next })
            return next
        })
    }

    const handleSelectSort = (next: NotificationSort) => {
        setSort(next)
        persistPrefs({ sort: next })
    }

    // У демо-строк нет записей на бэкенде — их не отправляем в API.
    const isMockNotification = (id: string) => id.startsWith('mock-')

    const handleMarkRead = useCallback((id: string) => {
        setNotifications((prev) => {
            const target = prev.find((row) => row.id === id)
            if (!target || !target.unread) {
                return prev
            }

            if (!isMockNotification(id)) {
                NotificationService.markRead(id).catch(() => {})
            }

            return prev.map((row) => (row.id === id ? { ...row, unread: false } : row))
        })
    }, [])

    // Клик по бейджу файла: открываем вложение в новой вкладке.
    // Файлы отдаются только с Bearer-токеном, поэтому качаем через axios и показываем object-URL.
    const handleOpenFile = useCallback(async (row: NotificationRow) => {
        const meta = row.meta
        if (!meta || meta.kind !== 'file') {
            return
        }

        handleMarkRead(row.id)

        // Вкладку открываем синхронно, в жесте клика — иначе браузер заблокирует попап.
        const fileWindow = window.open('', '_blank')

        try {
            let fileUrl = meta.fileUrl

            // Прямой ссылки в details нет — ищем вложение в списке файлов задачи.
            if (!fileUrl && row.taskId != null) {
                const data = await AttachmentService.list(Number(row.taskId))
                const attachments: any[] = Array.isArray(data) ? data : data?.attachments || []
                const attachment = attachments.find((file) => (
                    meta.attachmentId != null
                        ? String(file?.id) === String(meta.attachmentId)
                        : file?.filename === meta.label
                ))

                fileUrl = attachment?.file_url
            }

            const objectUrl = fileUrl ? await fetchAttachmentObjectUrl(fileUrl) : null

            if (!objectUrl) {
                fileWindow?.close()
                console.warn('[notifications] Файл вложения не найден — открыть нечего')
                return
            }

            if (fileWindow) {
                fileWindow.location.href = objectUrl
            } else {
                window.open(objectUrl, '_blank', 'noopener')
            }
        } catch (e) {
            fileWindow?.close()
            console.error('[notifications] Не удалось открыть файл вложения', e)
        }
    }, [handleMarkRead])

    // Проект уведомления может быть в другом рабочем пространстве — переключаемся на него,
    // иначе страница проекта не найдёт задачу и закроет модалку.
    const switchWorkspaceForProject = useCallback(async (projectId: string) => {
        await ensureWorkspaceForEntity({
            workspaceId: await fetchProjectWorkspaceId(projectId),
            currentWorkspace: workspace,
            workspaces,
            setWorkspace,
            fetchWorkspaces,
        })
    }, [workspace, workspaces, setWorkspace, fetchWorkspaces])

    // Клик по строке: отмечаем прочитанным и открываем задачу, к которой пришло уведомление.
    const handleRowClick = useCallback(async (row: NotificationRow) => {
        handleMarkRead(row.id)

        let taskLink = row.taskLink

        // Бэкенд не прислал ни task_link, ни project_id — узнаём проект у самой задачи.
        if (!taskLink && row.taskId != null) {
            try {
                const task = await TaskService.get(row.taskId)
                if (task?.project_id != null) {
                    taskLink = `/projects/${task.project_id}?view=board&task=${row.taskId}`
                }
            } catch (e) {
                console.error('[notifications] Не удалось получить задачу для перехода', e)
            }
        }

        if (!taskLink) {
            return
        }

        try {
            const url = new URL(taskLink, window.location.origin)

            if (url.origin === window.location.origin) {
                const projectMatch = url.pathname.match(/\/projects\/([^/?#]+)/)
                if (projectMatch) {
                    await switchWorkspaceForProject(projectMatch[1])
                }

                // Даём текущему клику завершиться: иначе обработчик "клика снаружи"
                // у модалки задачи поймает этот же клик и сразу её закроет.
                setTimeout(() => navigate(url.pathname + url.search), 0)
            } else {
                window.location.assign(url.toString())
            }
        } catch {
            // Некорректная ссылка от бэкенда — остаёмся на странице.
        }
    }, [handleMarkRead, navigate, switchWorkspaceForProject])

    const handleRemove = useCallback((id: string) => {
        setNotifications((prev) => {
            if (!isMockNotification(id)) {
                NotificationService.remove(id).catch(() => {})
            }

            return prev.filter((row) => row.id !== id)
        })
    }, [])

    // В бейджах табов показываем только непрочитанные.
    const tabCounts = useMemo(() => {
        const counts: Record<NotificationTab, number> = { task: 0, erp: 0, crm: 0 }
        for (const row of notifications) {
            if (row.unread) {
                counts[row.tab] += 1
            }
        }
        return counts
    }, [notifications])

    const tabRows = useMemo(
        () => notifications.filter((row) => row.tab === activeTab),
        [notifications, activeTab],
    )

    const groupedRows = useMemo((): { group: NotificationGroup | null; rows: NotificationRow[] }[] => {
        let rows = tabRows

        if (unreadOnly) {
            rows = rows.filter((row) => row.unread)
        }

        // Алфавитная сортировка — плоский список без группировки по датам.
        if (sort === 'alpha_asc' || sort === 'alpha_desc') {
            const sorted = rows
                .slice()
                .sort((a, b) => (a.target || a.author).localeCompare(b.target || b.author, 'ru'))

            if (sort === 'alpha_desc') {
                sorted.reverse()
            }

            return sorted.length ? [{ group: null, rows: sorted }] : []
        }

        const sortDesc = sort === 'date_desc'
        const rowsByGroup = new Map<NotificationGroup, NotificationRow[]>()
        for (const row of rows) {
            const groupRows = rowsByGroup.get(row.group) || []
            groupRows.push(row)
            rowsByGroup.set(row.group, groupRows)
        }

        const order = sortDesc ? NOTIFICATION_GROUP_ORDER : [...NOTIFICATION_GROUP_ORDER].reverse()

        return order
            .filter((group) => rowsByGroup.has(group))
            .map((group) => ({
                group: group as NotificationGroup | null,
                rows: rowsByGroup
                    .get(group)!
                    .slice()
                    .sort((a, b) => (sortDesc ? b.createdAtMs - a.createdAtMs : a.createdAtMs - b.createdAtMs)),
            }))
    }, [tabRows, unreadOnly, sort])

    return (
        <>
            <Header />
            <div className={styles.notifications}>
                <div className={styles.notifications__titleBar}>
                    <div className={styles.notifications__breadcrumbs}>
                        <span className={styles.notifications__breadcrumbsCurrent}>Уведомления</span>
                    </div>
                    <div className={styles.notifications__actions}>
                        <button
                            type="button"
                            className={`${styles.notifications__filterBtn} ${unreadOnly ? styles.notifications__filterBtnActive : ''}`}
                            onClick={handleToggleUnread}
                        >
                            <span className={styles.notifications__filterIcon}>
                                <BellUnreadIcon />
                            </span>
                            <span>Непрочитанные</span>
                            {unreadOnly && (
                                <span className={styles.notifications__filterClose}>
                                    <FilterCloseIcon />
                                </span>
                            )}
                        </button>
                        <DropDown
                            closeDropDownByPick
                            position="right"
                            btn={(isOpen: boolean) => (
                                <button type="button" className={`${styles.notifications__filterBtn} ${styles.notifications__sortBtn}`}>
                                    <span className={styles.notifications__filterIcon}>
                                        <SortArrowsIcon />
                                    </span>
                                    <span>{SORT_OPTIONS.find((option) => option.value === sort)?.label}</span>
                                    <span
                                        className={styles.notifications__filterChevron}
                                        style={{
                                            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.2s ease',
                                        }}
                                    >
                                        <ChevronDownIcon />
                                    </span>
                                </button>
                            )}
                        >
                            <div className={styles.notifications__menu}>
                                {SORT_OPTIONS.map((option) => (
                                    <div
                                        key={option.value}
                                        className={`${styles.notifications__menuItem} ${option.value === sort ? styles.notifications__menuItemActive : ''}`}
                                        onClick={() => handleSelectSort(option.value)}
                                    >
                                        <span>{option.label}</span>
                                    </div>
                                ))}
                            </div>
                        </DropDown>
                    </div>
                </div>

                <div className={styles.notifications__tabs}>
                    {NOTIFICATION_TABS.map((tab) => (
                        <button
                            key={tab.key}
                            type="button"
                            className={`${styles.notifications__tab} ${activeTab === tab.key ? styles.notifications__tabActive : ''}`}
                            onClick={() => setActiveTab(tab.key)}
                        >
                            {tab.label}
                            <span
                                className={`${styles.notifications__tabCount} ${activeTab === tab.key ? styles.notifications__tabCountActive : ''}`}
                            >
                                {tabCounts[tab.key]}
                            </span>
                        </button>
                    ))}
                </div>

                <div className={styles.notifications__list}>
                    {loading ? (
                        <div className={styles.notifications__empty}>Загрузка…</div>
                    ) : error ? (
                        <div className={styles.notifications__empty}>
                            Не удалось загрузить уведомления ({error}).{' '}
                            <button
                                type="button"
                                className={styles.notifications__inlineBtn}
                                onClick={loadNotifications}
                            >
                                Повторить
                            </button>
                        </div>
                    ) : groupedRows.length === 0 ? (
                        <div className={styles.notifications__empty}>
                            {unreadOnly && tabRows.length > 0 ? (
                                <>
                                    Непрочитанных уведомлений нет.{' '}
                                    <button
                                        type="button"
                                        className={styles.notifications__inlineBtn}
                                        onClick={handleToggleUnread}
                                    >
                                        Показать все
                                    </button>
                                </>
                            ) : (
                                'Уведомлений нет'
                            )}
                        </div>
                    ) : (
                        groupedRows.map(({ group, rows }) => (
                            <div
                                key={group ?? 'all'}
                                className={`${styles.notifications__group} ${group ? '' : styles.notifications__groupFlat}`}
                            >
                                {group && (
                                    <div className={styles.notifications__groupLabel}>
                                        {NOTIFICATION_GROUP_LABELS[group]}
                                    </div>
                                )}
                                {rows.map((row) => (
                                    <div
                                        key={row.id}
                                        className={`${styles.notifications__row} ${row.unread ? '' : styles.notifications__rowRead}`}
                                        onClick={() => handleRowClick(row)}
                                    >
                                        <div className={styles.notifications__author}>
                                            <NotificationAvatar row={row} />
                                            <span className={styles.notifications__authorName}>{row.author}</span>
                                        </div>
                                        <Divider style={{ width: 1, height: 30, flexShrink: 0 }} />
                                        <span className={styles.notifications__typeIcon}>
                                            {TYPE_ICONS[row.type]}
                                        </span>
                                        <Divider style={{ width: 1, height: 30, flexShrink: 0 }} />
                                        <div className={styles.notifications__content}>
                                            <span className={styles.notifications__action}>{row.action}</span>
                                            <span className={styles.notifications__target}>{row.target}</span>
                                            {row.meta && (
                                                <NotificationMetaBadges
                                                    meta={row.meta}
                                                    onOpenFile={
                                                        row.meta.kind === 'file' && (row.meta.fileUrl || row.taskId != null)
                                                            ? () => handleOpenFile(row)
                                                            : undefined
                                                    }
                                                />
                                            )}
                                        </div>
                                        <span className={styles.notifications__time}>
                                            {formatNotificationTime(row.createdAtMs, nowMs)}
                                        </span>
                                        <div className={styles.notifications__menuWrap} onClick={(event) => event.stopPropagation()}>
                                            <DropDown
                                                closeDropDownByPick
                                                position="right"
                                                btn={
                                                    <div className={styles.notifications__dotsBtn}>
                                                        <DotsVerticalIcon />
                                                    </div>
                                                }
                                            >
                                                <div className={styles.notifications__menu}>
                                                    {row.unread && (
                                                        <div
                                                            className={styles.notifications__menuItem}
                                                            onClick={() => handleMarkRead(row.id)}
                                                        >
                                                            <MarkAsReadIcon />
                                                            <span>Отметить как прочитанное</span>
                                                        </div>
                                                    )}
                                                    {(row.taskLink || row.taskId != null) && (
                                                        <div
                                                            className={styles.notifications__menuItem}
                                                            onClick={() => handleRowClick(row)}
                                                        >
                                                            <OpenSourceIcon />
                                                            <span>Открыть источник уведомления</span>
                                                        </div>
                                                    )}
                                                    <div
                                                        className={`${styles.notifications__menuItem} ${styles.notifications__menuItemDanger}`}
                                                        onClick={() => handleRemove(row.id)}
                                                    >
                                                        <TrashIcon />
                                                        <span>Удалить уведомление</span>
                                                    </div>
                                                </div>
                                            </DropDown>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </>
    )
}
